import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { Box, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation } from 'react-router-dom';
import equinetLogoImage from 'shared/assets/images/logo/equinet_logo.png';

import bgImage from '@/assets/images/bg-image.webp';
import { useAuthContext } from '@/context/AuthContext';
import { AuthPageLayout } from '@/layouts';
import { ROUTE } from '@/router/routes';
import { FirebaseAuthAPI } from '@/services/firebase/auth';
import { COLOR } from '@/theme/colors';

import { NewPasswordForm } from './components';

const EMAIL_QUERY_PARAM_NAME = 'email';
const TOKEN_QUERY_PARAM_NAME = 'token';

function ForgotPasswordPage() {
    const { invalidateUserAuth } = useAuthContext();
    const location = useLocation();
    const { t } = useTranslation();

    // A literal '+' in the query string (e.g. in a plus-addressed email, or a token that
    // happens to contain one) is decoded as a space by URLSearchParams — re-encode it
    // before parsing so it round-trips back to a '+' instead.
    const params = useMemo(
        () => new URLSearchParams(location.search.replace(/\+/g, '%2B')),
        [location.search],
    );

    const email = params.get(EMAIL_QUERY_PARAM_NAME);
    const token = params.get(TOKEN_QUERY_PARAM_NAME);

    const logOut = useCallback(async () => {
        await FirebaseAuthAPI.signOut();
        invalidateUserAuth();
    }, [invalidateUserAuth]);

    useEffect(() => {
        logOut();
    }, [logOut]);

    if (!email || !token) {
        return <Navigate replace to={ROUTE.signIn} />;
    }

    return (
        <AuthPageLayout
            backgroundImage={bgImage}
            rightColumnContent={
                <>
                    <Box
                        sx={{
                            mt: '35px',
                            ml: { xs: 4, md: 10 },
                        }}
                    >
                        <Link to={ROUTE.signIn}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <ArrowBackIosIcon
                                    sx={{ width: 12, height: 12 }}
                                    htmlColor={COLOR.ebonyClay}
                                />
                                <Typography
                                    fontSize={14}
                                    fontWeight={700}
                                    color={COLOR.ebonyClay}
                                >
                                    {t('ForgotPassword:topbar:back')}
                                </Typography>
                            </Box>
                        </Link>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            mt: '58px',
                        }}
                    >
                        <Box
                            sx={{
                                ml: { xs: 4, md: 10 },
                                mr: { xs: 4, md: 0 },
                            }}
                        >
                            <Box
                                alt="Equinet Logo"
                                component="img"
                                src={equinetLogoImage}
                                sx={{
                                    height: 88,
                                    mb: 4,
                                }}
                            />
                            <Typography
                                component="h1"
                                sx={{ fontWeight: 700, mb: 1 }}
                                variant="h5"
                            >
                                {t('NewPassword:placeholder:new_password')}
                            </Typography>
                        </Box>
                        <NewPasswordForm email={email} token={token} />
                    </Box>
                </>
            }
        />
    );
}

export default ForgotPasswordPage;
